import { React, useState } from 'react';

import { FormHelperText } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import MemoryRoundedIcon from '@mui/icons-material/MemoryRounded';
import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import PageHeader from '../../components/pageHeader';
import SettaSwitch from '../../components/settaSwitch';
import SubmitButton from '../../components/submitButton';
import { formatDateLabelPtBr, formatIsoDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/inverterActivation.css';

const DesativarReativarInversor = () => {
    const [serial, setSerial] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [reativar, setReativar] = useState(false);
    const [error, setError] = useState(false);
    const [dateInversor, setDateInversor] = useState(null);
    const [isSubmit, setIsSubmit] = useState(false);

    async function submitDesativarInversor (e) {
        e.preventDefault();
        setIsSubmit(true);

        let data = '';
        if (dateInversor) {
            setError(false);
            data = formatIsoDate(dateInversor);
        } else {
            setError(true);
            setIsSubmit(false);
            return;
        }

        const body = JSON.stringify({
            reativar,
            serial,
            dateInversor: data
        });

        try {
            const response = await fetchFlask('desativarReativarInversor', body);
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);
            if (response.status === 'success') {
                setSerial('');
                setDateInversor(null);
            }
        } finally {
            setIsSubmit(false);
        }
    };

    const actionLabel = reativar ? 'Reativar inversor' : 'Desativar inversor';
    const dateLabel = formatDateLabelPtBr(dateInversor);
    const completedFields = [serial, dateInversor].filter(Boolean).length;

    return (
        <Box className='mainBox ui-workflow-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                {actionLabel}
            </PageHeader>

            <form action='' method='POST' id='formDesativarReativarInversor' onSubmit={submitDesativarInversor} className='ui-workflow-layout'>
                <Box className='ui-workflow-workspace'>
                    <Box className='ui-workflow-intro'>
                        <Box>
                            <h2>Operação do inversor</h2>
                        </Box>
                        <p>
                            Informe o serial e a data da movimentação para registrar a alteração de status do inversor.
                        </p>
                    </Box>

                    <Box className='inverter-activation-mode-panel'>
                        <Box className='inverter-activation-mode-copy'>
                            {reativar ? <AutorenewRoundedIcon /> : <PowerSettingsNewRoundedIcon />}
                            <Box>
                                <span>Tipo de operação</span>
                                <strong>{actionLabel}</strong>
                            </Box>
                        </Box>

                        <Box className='inverter-activation-mode-switch'>
                            <span>Desativar</span>
                            <SettaSwitch
                                checked={reativar}
                                onChange={(checked) => setReativar(checked)}
                                ariaLabel='Alternar entre desativar e reativar inversor'
                            />
                            <span>Reativar</span>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <MemoryRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Identificação do inversor</strong>
                            </Box>
                        </Box>

                        <TextField
                            required
                            label='Serial Inversor'
                            value={serial}
                            onChange={(e) => setSerial(e.target.value)}
                        />
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <EventRepeatRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Data da alteração</strong>
                            </Box>
                        </Box>

                        <FormControl className='ui-control' error={error}>
                            <DayDatePicker
                                id='data'
                                label={reativar ? 'Data Reativação' : 'Data Desativação'}
                                minDate={new Date('2023-01-02')}
                                maxDate={new Date()}
                                value={dateInversor}
                                onChange={setDateInversor}
                            />
                            {error && (
                                <FormHelperText>Por favor selecione uma data.</FormHelperText>
                            )}
                        </FormControl>
                    </Box>
                </Box>

                <Box className='ui-workflow-summary'>
                    <Box className='ui-heading ui-heading-divided'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão da operação</strong>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-summary-list'>
                        <Box>
                            <span>Operação</span>
                            <strong>{actionLabel}</strong>
                        </Box>
                        <Box>
                            <span>Serial</span>
                            <strong className={!serial ? 'is-empty' : undefined}>{serial || 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Data</span>
                            <strong className={!dateInversor ? 'is-empty' : undefined}>{dateLabel}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-attention'>
                        <PowerSettingsNewRoundedIcon />
                        <p>
                            Confira a operação selecionada antes de enviar. O registro altera o status operacional do inversor informado.
                        </p>
                    </Box>

                    <Box className='ui-attention is-ok ui-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/2 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<SendRoundedIcon />}>
                        {actionLabel}
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default DesativarReativarInversor;
